import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  getDocument,
  GlobalWorkerOptions,
  PDFDocumentProxy,
  RenderTask,
} from "pdfjs-dist";
import { ChromePicker } from "react-color";
import type { ColorResult } from "react-color";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import "../styles/PDFPositionTool.css";
import { toast } from "react-toastify";
import { PDF_SCALE, PDF_FONT_SIZE_OFFSET, Y_OFFSET_CORRECTION } from "../constants/pdfConfig";
import { mmToPixels } from "../utils/units";
import { useNavigate, useLocation } from "react-router-dom";

GlobalWorkerOptions.workerSrc = 
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

interface PDFOption {
  url: string;
  fields: {
    [source: string]: string[];
  };
  firestoreDoc: string;
}

interface FacturePositionToolProps {
  pdfOptions: {
    [pdfName: string]: PDFOption;
  };
  defaultPdfName?: string;
}

type FieldData = {
  x: number;
  y: number;
  size?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
};

type PDFCoordinatesByPage = Record<string, Record<string, FieldData>>;

const FacturePositionTool: React.FC<FacturePositionToolProps> = ({ pdfOptions, defaultPdfName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hoverCoords, setHoverCoords] = useState<FieldData | null>(null);
  const [coordsByPage, setCoordsByPage] = useState<PDFCoordinatesByPage>({});
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string>(defaultPdfName || Object.keys(pdfOptions)[0]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>(null);

  // Définition des champs de la facture basés sur la table companies
  const factureFields = {
    "Informations Entreprise": [
      "companyName",
      "siret",
      "address",
      "postalCode",
      "city",
      "country"
    ],
    "Contact Principal": [
      "principalGender",
      "principalFirstName",
      "principalContact",
      "principalPhone",
      "principalEmail",
      "principalPosition"
    ],
    "Informations Facture": [
      "factureNumber",
      "factureDate",
      "dueDate",
      "totalHT",
      "totalTTC",
      "tvaRate",
      "tvaAmount"
    ]
  };

  // Declare drawLabels first as it's used by renderPage
  const drawLabels = useCallback((ctx: CanvasRenderingContext2D, height: number, currentCoordsByPage: PDFCoordinatesByPage, currentPage: number) => {
    const pageCoords = currentCoordsByPage[currentPage];
    if (!pageCoords) return;

    const yOffsetPixels = mmToPixels(Y_OFFSET_CORRECTION);

    Object.entries(pageCoords).forEach(([key, { x, y, size = 12, color, bold, italic }]) => {
      const fieldName = key.includes('-') ? key.split('-')[1] : key;
      let fontStyle = '';
      if (bold) fontStyle += 'bold ';
      if (italic) fontStyle += 'italic ';
      ctx.font = `${fontStyle}${size}px Arial`;
      ctx.fillStyle = color || "#cc0077";
      const adjustedY = y;
      ctx.fillText(
        fieldName, 
        x, 
        adjustedY - (size * PDF_FONT_SIZE_OFFSET) - yOffsetPixels
      );
    });
  }, [coordsByPage, currentPage, Y_OFFSET_CORRECTION, PDF_FONT_SIZE_OFFSET]);

  // Declare renderPage after drawLabels
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(currentPage);
      const scale = PDF_SCALE;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      if (renderTaskRef.current) renderTaskRef.current.cancel();

      const renderTask = page.render({
        canvasContext: context,
        viewport,
      });
      renderTaskRef.current = renderTask;

      await renderTask.promise;
      renderTaskRef.current = null;
      drawLabels(context, canvas.height, coordsByPage, currentPage);
    } catch (err) {
      if ((err as Error).name !== "RenderingCancelledException") {
        console.error("PDF rendering error:", err);
        toast.error("Erreur lors du rendu du PDF");
      }
    }
  }, [pdfDoc, currentPage, drawLabels, coordsByPage]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserEmail(user?.email || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const localData = localStorage.getItem("factureCoordinatesByPage");
        if (localData) setCoordsByPage(JSON.parse(localData));

        if (userEmail) {
          const docName = pdfOptions[selectedPdf].firestoreDoc;
          const docRef = doc(db, "Coordinates", docName);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists() && docSnap.data()?.coordinates) {
            const firestoreData = docSnap.data().coordinates as PDFCoordinatesByPage;
            setCoordsByPage(firestoreData);
          }
        }

        const loadingTask = getDocument(pdfOptions[selectedPdf].url);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
      } catch (err) {
        console.error("Initial load error:", err);
        toast.error("Erreur lors du chargement initial");
      }
    };

    loadInitialData();
  }, [selectedPdf, userEmail, pdfOptions]);

  // Effect for rendering page and drawing labels
  useEffect(() => {
    renderPage();
  }, [pdfDoc, currentPage, drawLabels, renderPage]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);
    
    setHoverCoords({ x, y });
  };

  const isFieldUsed = (fieldName: string): boolean => {
    return coordsByPage[currentPage]?.[fieldName] !== undefined;
  };

  const sortedFields = useMemo(() => {
    let sortableFields = coordsByPage[currentPage] ? Object.entries(coordsByPage[currentPage]) : [];
    if (sortConfig !== null) {
      sortableFields.sort(([, a], [, b]) => {
        const aValue = a[sortConfig.key as keyof FieldData];
        const bValue = b[sortConfig.key as keyof FieldData];

        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          const aStr = String(aValue);
          const bStr = String(bValue);
          return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        }
      });
    }
    return sortableFields;
  }, [coordsByPage, currentPage, sortConfig]);

  const requestSort = (key: string) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return '';
    }
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const saveCoordinatesToFirestore = async (coordinates: PDFCoordinatesByPage) => {
    if (!userEmail) {
      toast.error("Vous devez être connecté pour enregistrer");
      return;
    }

    setIsSaving(true);
    try {
      const docName = pdfOptions[selectedPdf].firestoreDoc;
      const coordinatesRef = doc(db, "Coordinates", docName);
      
      await setDoc(coordinatesRef, {
        coordinates,
        metadata: {
          lastUpdated: serverTimestamp(),
          updatedBy: userEmail,
          pdfName: selectedPdf
        }
      }, { merge: true });
      
      toast.success(`Coordonnées enregistrées dans ${docName}`);
    } catch (error) {
      console.error("Erreur d'enregistrement :", error);
      toast.error("Échec de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const updateCoord = (key: string, field: keyof FieldData, value: number | string | boolean) => {
    const updated = {
      ...coordsByPage,
      [currentPage]: {
        ...coordsByPage[currentPage],
        [key]: {
          ...(coordsByPage[currentPage]?.[key] || { x: 0, y: 0, size: 12 }),
          [field]: value,
        },
      },
    };
    setCoordsByPage(updated);
    localStorage.setItem("factureCoordinatesByPage", JSON.stringify(updated));
  };

  const handleColorChange = (key: string, color: ColorResult) => {
    updateCoord(key, "color", color.hex);
    setColorPickerOpen(null);
  };

  const removeField = (key: string) => {
    const updated = { ...coordsByPage };
    if (updated[currentPage]?.[key]) {
      delete updated[currentPage][key];
      setCoordsByPage(updated);
      localStorage.setItem("factureCoordinatesByPage", JSON.stringify(updated));
      
      if (userEmail) {
        const docName = pdfOptions[selectedPdf].firestoreDoc;
        const docRef = doc(db, "Coordinates", docName);
        setDoc(docRef, {
          coordinates: updated,
          metadata: {
            lastUpdated: serverTimestamp(),
            updatedBy: userEmail
          }
        }, { merge: true })
        .then(() => toast.success(`Champ supprimé de Firestore : ${key.split('-')[1]}`))
        .catch((err) => {
          console.error("Erreur suppression champ Firestore :", err);
          toast.error("Erreur lors de la suppression du champ de Firestore.");
        });
      }
      
      toast.success(`Champ supprimé: ${key.split('-')[1]}`);
    }
  };

  const addFieldFromHover = async () => {
    if (!selectedField || !hoverCoords) return;

    const isExistingField = isFieldUsed(selectedField);

    if (isExistingField && !window.confirm(
      `Ce champ est déjà positionné. Voulez-vous le déplacer à X=${hoverCoords.x}, Y=${hoverCoords.y} ?`
    )) return;

    const updated = {
      ...coordsByPage,
      [currentPage]: {
        ...coordsByPage[currentPage],
        [selectedField]: {
          ...(coordsByPage[currentPage]?.[selectedField] || { 
            size: 12, 
            color: "#cc0077",
            bold: false,
            italic: false
          }),
          x: hoverCoords.x,
          y: Math.round(hoverCoords.y / PDF_SCALE),
        },
      },
    };
    
    setCoordsByPage(updated);
    localStorage.setItem("factureCoordinatesByPage", JSON.stringify(updated));
    toast.success(isExistingField 
      ? `Position du champ "${selectedField.split('-')[1]}" mise à jour`
      : `Champ "${selectedField.split('-')[1]}" ajouté`
    );
    setSelectedField(null);
  };

  const resetAllCoordinates = async () => {
    if (!window.confirm("Supprimer toutes les coordonnées enregistrées ?")) return;

    try {
      localStorage.removeItem("factureCoordinatesByPage");
      if (userEmail) {
        const docName = pdfOptions[selectedPdf].firestoreDoc;
        await setDoc(doc(db, "Coordinates", docName), {
          coordinates: {},
          metadata: {
            lastUpdated: serverTimestamp(),
            updatedBy: userEmail
          }
        });
      }
      setCoordsByPage({});
      toast.success("Coordonnées réinitialisées");
    } catch (err) {
      console.error("Erreur lors de la réinitialisation :", err);
      toast.error("Erreur lors de la suppression des coordonnées");
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    // Changer le PDF affiché en interne
    switch(value) {
      case 'MonDevis.pdf':
        navigate('/admin/pdf-tool');
        break;
      case 'DevisFoodinnov.pdf':
        // TODO: Ajouter la redirection vers la page de devis Foodinnov quand elle sera créée
        console.log('Redirection vers devis Foodinnov à implémenter');
        break;
      case 'hillerbrandfdaregistration.pdf':
        navigate('/outil-fda-pdf');
        break;
      case 'Facture.pdf':
          // Si on sélectionne la facture sur la page facture, on ne fait rien
          break;
      default:
        // Gérer le cas où l'option vide est sélectionnée (ne rien faire ou rediriger vers une page d'accueil de l'outil)
        // Ou potentiellement naviguer vers la page d'accueil des outils si l'option par défaut est choisie
        // navigate('/admin/pdf-rep-tool'); // Exemple de redirection vers la page d'accueil des outils
        break;
    }
  };

  const fieldGroups = Object.entries(factureFields).map(([source, fields]) => (
    <optgroup key={source} label={source}>
      {fields.map((field) => {
        const fullFieldName = `${source}-${field}`;
        const isUsed = isFieldUsed(fullFieldName);
        return (
          <option 
            key={fullFieldName} 
            value={fullFieldName}
            className={isUsed ? "field-used" : "field-available"}
          >
            {field} {isUsed && "(déjà utilisé)"}
          </option>
        );
      })}
    </optgroup>
  ));

  return (
    <div className="pdf-position-tool-container" style={{ paddingTop: '80px' }}>
      <h2>Outil de Repérage Facture</h2>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <select
          value={selectedPdf}
          onChange={handlePdfChange}
          className="pdf-rep-select"
        >
          <option value="">Sélectionnez un type de document</option>
          <option value="MonDevis.pdf">Mon Devis</option>
          <option value="hillerbrandfdaregistration.pdf">Hillerbrand FDA</option>
          <option value="Facture.pdf">Ma Facture</option>
        </select>
      </div>
      <p className="pdf-instruction">
        Cliquez sur le PDF pour obtenir les coordonnées <code>X / Y</code> et modifiez-les à droite.
      </p>

      <div className="pdf-body">
        <div className="pdf-viewer-container">
          <canvas
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            className="pdf-canvas"
          />
        </div>

        <div className="pdf-coord-fields">
          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>
                ◀
              </button>
              <span>Page {currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>
                ▶
              </button>
            </div>
          )}

          <div className="pdf-coord-fields-content">
            {hoverCoords && (
              <div className="pdf-coordinates">
                Curseur : <strong>X = {hoverCoords.x}</strong>,{" "}
                <strong>Y = {hoverCoords.y}</strong>
              </div>
            )}

            <h3>Coordonnées des champs Facture</h3>
            <table className="coords-table">
              <thead>
                <tr>
                  <th>Champ</th>
                  <th>X</th>
                  <th>Y</th>
                  <th>Taille</th>
                  <th>Couleur</th>
                  <th>Gras</th>
                  <th>Italic</th>
                  <th>Suppr.</th>
                </tr>
              </thead>
              <tbody>
                {coordsByPage[currentPage] && Object.entries(coordsByPage[currentPage])
                  .map(([key, { x, y, size, color, bold, italic }]) => (
                    <tr key={key}>
                      <td>{key.includes('-') ? key.split('-')[1] : key}</td>
                      <td>
                        <input
                          type="number"
                          value={x}
                          onChange={(e) => updateCoord(key, "x", parseInt(e.target.value) || 0)}
                          placeholder="X"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={y}
                          onChange={(e) => updateCoord(key, "y", parseInt(e.target.value) || 0)}
                          placeholder="Y"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={size ?? 12}
                          onChange={(e) => updateCoord(key, "size", parseInt(e.target.value) || 12)}
                          placeholder="Taille"
                        />
                      </td>
                      <td>
                        <div className="color-picker-container">
                          <div
                            className="color-preview"
                            style={{ backgroundColor: color || "#cc0077" }}
                            onClick={() => setColorPickerOpen(key)}
                          />
                          {colorPickerOpen === key && (
                            <div className="color-popover">
                              <div
                                className="color-cover"
                                onClick={() => setColorPickerOpen(null)}
                              />
                              <ChromePicker
                                color={color || "#cc0077"}
                                onChangeComplete={(color) => handleColorChange(key, color)}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <label className="style-checkbox">
                          <input
                            type="checkbox"
                            checked={!!bold}
                            onChange={(e) => updateCoord(key, "bold", e.target.checked)}
                          />
                        </label>
                      </td>
                      <td>
                         <label className="style-checkbox">
                          <input
                            type="checkbox"
                            checked={!!italic}
                            onChange={(e) => updateCoord(key, "italic", e.target.checked)}
                          />
                        </label>
                      </td>
                      <td>
                        <button
                          className="remove-field-btn"
                          onClick={() => removeField(key)}
                          title="Supprimer ce champ"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
    
            <div className="pdf-field-adder">
              <select
                value={selectedField || ""}
                onChange={(e) => setSelectedField(e.target.value || null)}
              >
                <option value="">-- Ajouter un champ --</option>
                {fieldGroups}
              </select>
              
              <button
                onClick={addFieldFromHover}
                disabled={!hoverCoords || !selectedField}
              >
                ➕ {selectedField ? `Ajouter/mettre à jour ${selectedField.split('-')[1]}` : "Ajouter un champ"}
              </button>
              
              <div className="pdf-actions">
                <button 
                  onClick={() => saveCoordinatesToFirestore(coordsByPage)}
                  disabled={isSaving || !userEmail}
                  className="save-button"
                >
                  {isSaving ? "Enregistrement..." : "Enregistrer dans Firestore"}
                </button>
                
                {!userEmail && (
                  <p className="auth-warning">
                    Connectez-vous pour activer l'enregistrement
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturePositionTool; 